import * as React from 'react';
import classnames from 'classnames';
import isNil from 'lodash/isNil';

import { TableCell, TableRow } from 'components/table';
import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';
import Entity from 'components/entity/Entity';
import EntityAvatar from 'components/entity/entity-avatar/EntityAvatar';
import EntityName from 'components/entity/entity-name/EntityName';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import UserNotificationsRow from './user-notifications-row/UserNotificationsRow';
import UserNotificationsRowCollection from './user-notifications-row-collection/UserNotificationsRowCollection';

import { getEntityFromProfile } from 'utils/view';

import type {
  TextEntry,
  UserNotification,
  UserNotificationType,
  UserNotificationUserMeta,
  UserNotificationUserMetaRaw,
  UserNotificationsGrouped
} from 'types';

type Props = {
  groupedNotifications: UserNotificationsGrouped,
  collectionNotifications: UserNotificationUserMeta,
  onNotificationChange: (
    notificationId: number,
    allowNotifications: boolean,
    type: UserNotificationType
  ) => void,
  onGroupChange: (
    notifications: Array<UserNotification>,
    collectionNotifications: UserNotificationUserMeta,
    type: UserNotificationType
  ) => void
};

type State = {
  isExpanded: {
    [x: string]: boolean
  }
};

const NUM_ROWS_TO_SHOW = 6;
const ROW_HEIGHT = 57; // 56px + 1px border
const MAX_CONTAINER_HEIGHT = 370; // includes 6 rows plus half of 7th row

class UserNotificationsRowsGrouped extends React.PureComponent<Props, State> {
  state = {
    isExpanded: {}
  };

  constructor(props: Props) {
    super(props);

    const { groupedNotifications } = props;
    const notificationKeys = Object.keys(groupedNotifications);

    // if only one group, expand the container
    if (notificationKeys.length === 1) {
      this.state.isExpanded = {
        [notificationKeys[0]]: true
      };
    }
  }

  getOrgId(key: string): number | undefined {
    const { groupedNotifications } = this.props;
    const notifications = groupedNotifications[key] || [];

    return notifications[0]?.campaign?.brandProfile?.organizationId;
  }

  getNotifications(key: string): Array<UserNotification> {
    const { groupedNotifications } = this.props;

    return groupedNotifications[key] || [];
  }

  getCollectionNotification(key: string): UserNotificationUserMetaRaw | null {
    const { collectionNotifications } = this.props;
    const orgId = this.getOrgId(key);

    return !isNil(orgId) && !isNil(collectionNotifications)
      ? collectionNotifications[orgId]
      : null;
  }

  getExpandableClass(baseClass: string, key: string): string {
    const { isExpanded } = this.state;

    return classnames(baseClass, {
      [`${baseClass}--expanded`]: isExpanded[key]
    });
  }

  getContainerHeight(notificationsLength: number, key: string): number {
    const { isExpanded } = this.state;
    const combinedRowHeights = isExpanded[key] ? ROW_HEIGHT * notificationsLength : 0;

    if (notificationsLength > NUM_ROWS_TO_SHOW) {
      return combinedRowHeights > MAX_CONTAINER_HEIGHT ? MAX_CONTAINER_HEIGHT : combinedRowHeights;
    }

    return combinedRowHeights;
  }

  getUpdatedCollectionNotifications(key: string, isChecked: boolean): UserNotificationUserMeta {
    const { collectionNotifications } = this.props;
    const orgId = this.getOrgId(key);
    let updatedCollectionNotifications = collectionNotifications;

    if (!isNil(orgId)) {
      updatedCollectionNotifications = {
        [orgId]: {
          ...collectionNotifications[orgId],
          status: !isChecked ? 'enabled' : 'disabled'
        }
      };
    }

    return updatedCollectionNotifications;
  }

  handleExpandable = (event: React.SyntheticEvent<HTMLElement>): void => {
    const target = event.currentTarget;
    const key = target.dataset.key;

    if (key === undefined) {
      return;
    }

    this.setState(state => ({
      isExpanded: {
        [key]: !state.isExpanded[key]
      }
    }));
  };

  handleBrandCheckboxClick = (event: React.SyntheticEvent<TextEntry>): void => {
    const { groupedNotifications, onGroupChange, onNotificationChange } = this.props;
    const target = event.currentTarget;
    const key = target.dataset.key;
    const isChecked = target.dataset.checked === 'true';

    if (key === undefined) {
      return;
    }

    const collectionNotification = this.getCollectionNotification(key);

    const updatedNotifications = groupedNotifications[key].map(notification => ({
      ...notification,
      allowNotifications: !isChecked
    }));

    // handle collection notification update
    const orgId = this.getOrgId(key);
    const updatedCollectionNotifications = this.getUpdatedCollectionNotifications(key, isChecked);

    // collections (creator proposal)
    if (!isNil(orgId) && !isNil(collectionNotification)) {
      onNotificationChange(orgId, !isChecked, 'creator_proposal');
    }

    // campaign
    onGroupChange(updatedNotifications, updatedCollectionNotifications, 'campaign');
  };

  handleNotificationCheckboxClick = (
    notificationId: number,
    allowNotifications: boolean,
    type: UserNotificationType
  ): void => {
    const { onNotificationChange } = this.props;

    onNotificationChange(notificationId, allowNotifications, type);
  };

  renderCampaignEntity = (notification: UserNotification): JSX.Element => {
    const { campaign } = notification;
    const brandProfile = campaign.brandProfile || {};
    const entity = getEntityFromProfile(brandProfile);
    const entityUrl = `/brand/profiles/${brandProfile.id}`;

    return (
      <Entity>
        <EntityAvatar
          bordered
          entity={entity}
          link={entityUrl}
          size="medium"
        />
        <EntityName
          maxLength={350}
          style={{
            maxWidth: 350,
            fontWeight: 600,
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap'
          }}
          multiline
          size="small"
          entity={entity}
          link={entityUrl}
        />
      </Entity>
    );
  };

  renderGroup = (key: string): JSX.Element => {
    const notifications = this.getNotifications(key);
    const collectionNotification = this.getCollectionNotification(key);
    const isGroupChecked = (isNil(collectionNotification) || collectionNotification?.status === 'enabled')
      && notifications.every(notification => notification.allowNotifications);

    return (
      <React.Fragment key={`notification-grouped-row-${key}`}>
        <div className={this.getExpandableClass('user-notifications-rows-grouped__group', key)}>
          <TableRow>
            <TableCell
              width="nano"
              padding="none"
            >
              <div
                className={this.getExpandableClass('user-notifications-rows-grouped__icon', key)}
                onClick={this.handleExpandable}
                data-key={key}
              >
                <SVGDynamicIcon
                  name="chevron-down-custom-fg"
                  width={14}
                  height={14}
                />
              </div>
            </TableCell>
            <TableCell width="fill">
              {this.renderCampaignEntity(notifications[0])}
            </TableCell>
            <TableCell width="medium">
              <InputCheckbox
                id={`checkbox-${key}`}
                name={`checkbox-${key}`}
                checked={isGroupChecked}
                label="Receive emails"
                size="medium-small"
                onChange={this.handleBrandCheckboxClick}
                data={{ key, checked: isGroupChecked }}
              />
            </TableCell>
          </TableRow>
        </div>
        {this.renderNotifications(key)}
      </React.Fragment>
    );
  };

  renderNotifications(key: string): React.ReactNode {
    const notifications = this.getNotifications(key);
    const hasSharedAccess = notifications.some(notification => notification.campaign.hasSharedAccess);
    const containerHeight = this.getContainerHeight(notifications.length + (hasSharedAccess ? 1 : 0), key);

    return (
      <div
        className={this.getExpandableClass('user-notifications-rows-grouped__notification-container', key)}
        style={{ height: containerHeight }}
      >
        {
          hasSharedAccess &&
            this.renderNotificationCollection(key)
        }
        {
          notifications.map(notification => (
            <div
              key={`notification-row-${notification.id}`}
              className={this.getExpandableClass('user-notifications-rows-grouped__notification', key)}
            >
              <UserNotificationsRow
                notification={notification}
                onChange={this.handleNotificationCheckboxClick}
              />
            </div>
          ))
        }
      </div>
    );
  }

  renderNotificationCollection(key: string): React.ReactNode {
    const orgId = this.getOrgId(key);
    const collectionNotification = this.getCollectionNotification(key);

    if (isNil(collectionNotification)) {
      return null;
    }

    return (
      <div
        key={`notification-row---collection-${key}`}
        className={this.getExpandableClass('user-notifications-rows-grouped__notification', key)}
      >
        <UserNotificationsRowCollection
          orgId={orgId}
          notification={collectionNotification}
          onChange={this.handleNotificationCheckboxClick}
        />
      </div>
    );
  }

  render(): React.ReactNode {
    const { groupedNotifications } = this.props;

    return Object.keys(groupedNotifications).map(this.renderGroup);
  }
}

export default UserNotificationsRowsGrouped;
